import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MdPersonAdd, MdEdit, MdBlock, MdCheckCircle, MdClose, MdSave, MdAdminPanelSettings, MdPerson } from 'react-icons/md';
import { usuariosAPI, metasAPI } from '../services/api';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

// ── Layout ──────────────────────────────────────────────────────────────────

const Container = styled.div`
  width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    margin-bottom: ${p => p.theme.spacing.md};
  }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    font-size: ${p => p.theme.fontSizes.xxl};
  }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow: hidden;
`;

// ── Buttons ──────────────────────────────────────────────────────────────────

const PrimaryBtn = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;
  svg { font-size: 1.2rem; }
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px ${p => p.theme.colors.primary}50; }
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  min-height: unset;
  border-radius: ${p => p.theme.borderRadius.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: ${p => p.theme.transitions.fast};
  &:hover { background: ${p => p.theme.colors.surface}; color: ${p => p.theme.colors.textPrimary}; }
  &.danger:hover { background: ${p => p.theme.colors.danger}15; color: ${p => p.theme.colors.danger}; border-color: ${p => p.theme.colors.danger}40; }
  &.success:hover { background: ${p => p.theme.colors.success}15; color: ${p => p.theme.colors.success}; border-color: ${p => p.theme.colors.success}40; }
`;

// ── Table ─────────────────────────────────────────────────────────────────────

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${p => p.theme.fontSizes.sm};
`;

const Th = styled.th`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.textMuted};
  background: ${p => p.theme.colors.background};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  color: ${p => p.theme.colors.textPrimary};
  vertical-align: middle;
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:last-child td { border-bottom: none; }
  &:hover td { background: ${p => p.theme.colors.background}; }
`;

// ── Badges ────────────────────────────────────────────────────────────────────

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${p => p.color}18;
  color: ${p => p.color};
  border: 1px solid ${p => p.color}30;
  svg { font-size: 0.75rem; }
`;

// ── Modal ─────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${p => p.theme.spacing.md};
`;

const Modal = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.xl};
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.xl};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  h3 { margin: 0; font-size: ${p => p.theme.fontSizes.lg}; color: ${p => p.theme.colors.textPrimary}; }
`;

const ModalBody = styled.div`
  padding: ${p => p.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.md};
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.sm};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  label { font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textSecondary}; }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}20; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}20; }
`;

const SaveBtn = styled.button`
  padding: 9px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  &:disabled { opacity: 0.6; }
`;

const CancelBtn = styled.button`
  padding: 9px ${p => p.theme.spacing.lg};
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
`;

const ErrorMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  margin: 0;
`;

// ── Component ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = { nombre: '', email: '', password: '', rol: 'usuario', meta_ingresos: '', meta_unidades: '', meta_leads: '' };

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = crear, obj = editar
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const res = await usuariosAPI.getAll();
      setUsuarios(res.data?.data || []);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setError('');
    setModalOpen(true);
  };

  const openEdit = async (u) => {
    setEditTarget(u);
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol, meta_ingresos: '', meta_unidades: '', meta_leads: '' });
    setError('');
    setModalOpen(true);
    try {
      const r = await metasAPI.getByUsuario(u.id);
      const meta = r.data?.data;
      if (meta?.meta_ingresos != null) {
        setForm(f => ({ ...f, meta_ingresos: meta.meta_ingresos, meta_unidades: meta.meta_unidades ?? '', meta_leads: meta.meta_leads ?? '' }));
      }
    } catch { /* sin meta aún */ }
  };

  const closeModal = () => setModalOpen(false);

  const handleSave = async () => {
    setError('');
    if (!form.nombre.trim() || !form.email.trim()) {
      setError('Nombre y email son obligatorios.');
      return;
    }
    if (!editTarget && !form.password) {
      setError('La contraseña es obligatoria para nuevos usuarios.');
      return;
    }
    try {
      setSaving(true);
      const payload = { nombre: form.nombre, email: form.email, rol: form.rol };
      if (form.password) payload.password = form.password;

      if (editTarget) {
        await usuariosAPI.update(editTarget.id, payload);
        if (form.meta_ingresos !== '' || form.meta_unidades !== '' || form.meta_leads !== '') {
          await metasAPI.set(editTarget.id, {
            meta_ingresos: parseFloat(form.meta_ingresos) || 0,
            meta_unidades: parseInt(form.meta_unidades) || 0,
            meta_leads:    parseInt(form.meta_leads)    || 0,
          });
        }
      } else {
        payload.password = form.password;
        await usuariosAPI.create(payload);
      }
      closeModal();
      fetchUsuarios();
    } catch (e) {
      setError(e?.message || 'Error al guardar usuario.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (u) => {
    try {
      if (u.activo) {
        await usuariosAPI.deactivate(u.id);
      } else {
        await usuariosAPI.reactivate(u.id);
      }
      fetchUsuarios();
    } catch (e) {
      alert(e?.message || 'Error al cambiar estado del usuario.');
    }
  };

  const getRolColor = (rol) =>
    rol === 'admin' ? '#7c3aed' : '#0891b2';

  return (
    <Container>
      <PageHeader>
        <Title>Usuarios</Title>
        <PrimaryBtn onClick={openCreate}>
          <MdPersonAdd /> Nuevo usuario
        </PrimaryBtn>
      </PageHeader>

      <Card>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Email</Th>
                <Th>Rol</Th>
                <Th>Estado</Th>
                <Th>Últ. acceso</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonTableRows cols={6} rows={4} />
              ) : usuarios.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState message="No hay usuarios registrados" />
                  </td>
                </tr>
              ) : (
                usuarios.map(u => (
                  <Tr key={u.id}>
                    <Td style={{ fontWeight: 600 }}>{u.nombre}</Td>
                    <Td style={{ color: 'inherit', opacity: 0.75 }}>{u.email}</Td>
                    <Td>
                      <Badge color={getRolColor(u.rol)}>
                        {u.rol === 'admin' ? <MdAdminPanelSettings /> : <MdPerson />}
                        {u.rol}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge color={u.activo ? '#16a34a' : '#6b7280'}>
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </Td>
                    <Td style={{ opacity: 0.65 }}>
                      {u.ultimo_acceso
                        ? new Date(u.ultimo_acceso).toLocaleDateString('es-MX')
                        : '—'}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <IconBtn title="Editar" onClick={() => openEdit(u)}>
                          <MdEdit />
                        </IconBtn>
                        <IconBtn
                          title={u.activo ? 'Desactivar' : 'Reactivar'}
                          className={u.activo ? 'danger' : 'success'}
                          onClick={() => handleToggleActivo(u)}
                        >
                          {u.activo ? <MdBlock /> : <MdCheckCircle />}
                        </IconBtn>
                      </div>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </Card>

      {modalOpen && (
        <Overlay onClick={closeModal}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editTarget ? 'Editar usuario' : 'Nuevo usuario'}</h3>
              <IconBtn onClick={closeModal}><MdClose /></IconBtn>
            </ModalHeader>
            <ModalBody>
              {error && <ErrorMsg>{error}</ErrorMsg>}
              <FormGroup>
                <label>Nombre</label>
                <Input
                  value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Nombre completo"
                />
              </FormGroup>
              <FormGroup>
                <label>Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="correo@ejemplo.com"
                />
              </FormGroup>
              <FormGroup>
                <label>{editTarget ? 'Nueva contraseña (opcional)' : 'Contraseña'}</label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={editTarget ? 'Dejar vacío para no cambiar' : 'Mínimo 6 caracteres'}
                />
              </FormGroup>
              <FormGroup>
                <label>Rol</label>
                <Select value={form.rol} onChange={e => setForm(f => ({ ...f, rol: e.target.value }))}>
                  <option value="usuario">Usuario</option>
                  <option value="admin">Administrador</option>
                </Select>
              </FormGroup>
              {editTarget && (
                <>
                  <div style={{ borderTop: '1px solid', borderColor: 'inherit', opacity: 0.15, margin: '4px 0' }} />
                  <FormGroup>
                    <label>Meta mensual de ingresos (mes actual)</label>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={form.meta_ingresos}
                      onChange={e => setForm(f => ({ ...f, meta_ingresos: e.target.value }))}
                      placeholder="Ej. 50000"
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Meta mensual de unidades vendidas (productos/servicios)</label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={form.meta_unidades}
                      onChange={e => setForm(f => ({ ...f, meta_unidades: e.target.value }))}
                      placeholder="Ej. 10"
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Meta mensual de leads ganados</label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={form.meta_leads}
                      onChange={e => setForm(f => ({ ...f, meta_leads: e.target.value }))}
                      placeholder="Ej. 5"
                    />
                  </FormGroup>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={closeModal}>Cancelar</CancelBtn>
              <SaveBtn onClick={handleSave} disabled={saving}>
                <MdSave /> {saving ? 'Guardando...' : 'Guardar'}
              </SaveBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default Usuarios;
